import React, { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_BADGE } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

const Profile = () => {
  const { t } = useLang();
  const { user, checkAuth } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 6) { toast.error(t("password_min")); return; }
    if (next !== confirm) { toast.error(t("password_mismatch")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: user?.has_password ? current : null,
        new_password: next,
      });
      toast.success(t("password_changed"));
      setCurrent(""); setNext(""); setConfirm("");
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl" data-testid="profile-view">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("profile")}</h2>
        <p className="text-sm text-slate-500 mt-1">{user?.email}</p>
      </div>

      <Card className="p-5 border-slate-200 shadow-sm space-y-1 text-sm">
        <div className="flex justify-between py-1">
          <span className="text-slate-500">{t("name")}</span>
          <span className="font-medium text-slate-900">{user?.name}</span>
        </div>
        <div className="flex justify-between py-1">
          <span className="text-slate-500">{t("email")}</span>
          <span className="font-medium text-slate-900">{user?.email}</span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-slate-500">{t("role")}</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${ROLE_BADGE[user?.role] || ROLE_BADGE.viewer}`} data-testid="profile-role-badge">
            {t(user?.role || "viewer")}
          </span>
        </div>
      </Card>

      <Card className="p-5 border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-orange-500" />
          <h3 className="text-base font-semibold text-slate-900">{t("change_password")}</h3>
        </div>

        {!user?.has_password && (
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="set-password-notice">
            {t("set_password_notice")}
          </p>
        )}

        {user?.has_password && (
          <div>
            <Label className="text-xs">{t("current_password")}</Label>
            <Input type="password" data-testid="current-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
        )}
        <div>
          <Label className="text-xs">{t("new_password")}</Label>
          <Input type="password" data-testid="new-password-input" value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("confirm_password")}</Label>
          <Input type="password" data-testid="confirm-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <Button onClick={submit} disabled={saving} data-testid="save-new-password-button" className="bg-orange-500 hover:bg-orange-600">
          {t("save")}
        </Button>
      </Card>
    </div>
  );
};

export default Profile;
